import React from 'react';
import { Heart, Briefcase, PartyPopper, Palette, Users, Cake, Baby, Sparkles, Building2, Camera, Music, Utensils } from 'lucide-react';
import { Button } from './ui/button';

const Services = () => {
  const mainServices = [
    {
      icon: <Heart size={48} />,
      title: 'Wedding Planning & Management',
      description: 'Complete wedding planning from engagement to reception. Traditional ceremonies, modern celebrations, vendor coordination, venue selection, and flawless execution for your dream wedding.',
      image: 'https://customer-assets.emergentagent.com/job_made-in-heaven/artifacts/blu7pxhk_bab35fe0-8548-4b81-9fb3-0569ce84f624.jpeg',
      services: ['Full Wedding Planning', 'Haldi & Mehendi', 'Sangeet', 'Reception', 'Vendor Management']
    },
    {
      icon: <PartyPopper size={48} />,
      title: 'Social & Family Celebrations',
      description: 'Birthdays, anniversaries, engagements, baby showers, and family gatherings. Personalized planning with attention to detail for intimate celebrations and large parties.',
      image: 'https://customer-assets.emergentagent.com/job_made-in-heaven/artifacts/55feew7c_9d238921-ef87-45e3-a9c0-bbe83467ed5b.jpeg',
      services: ['Birthdays', 'Anniversaries', 'Baby Showers', 'Engagement Parties', 'Private Events']
    },
    {
      icon: <Briefcase size={48} />,
      title: 'Corporate Event Management',
      description: 'Professional event management for conferences, seminars, product launches, team events, and corporate gatherings. Strategic planning with seamless coordination.',
      image: 'https://images.unsplash.com/photo-1653821355736-0c2598d0a63e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxldmVudCUyMHBsYW5uaW5nfGVufDB8fHx8MTc3MzcxMjQwM3ww&ixlib=rb-4.1.0&q=85',
      services: ['Conferences', 'Product Launches', 'Team Building', 'Corporate Parties', 'Seminars']
    },
    {
      icon: <Palette size={48} />,
      title: 'Decor & Styling',
      description: 'Stunning decoration and styling services including floral arrangements, lighting design, stage setups, and complete venue transformation tailored to your theme.',
      image: 'https://customer-assets.emergentagent.com/job_made-in-heaven/artifacts/u6cwlur4_9b471af2-805d-4b36-8f19-40bc08944a22.jpeg',
      services: ['Floral Decor', 'Lighting Design', 'Stage Setup', 'Theme Styling', 'Venue Transform']
    },
  ];

  const additionalServices = [
    { icon: <Building2 size={32} />, title: 'Venue Coordination', description: 'Find and book the perfect venue' },
    { icon: <Users size={32} />, title: 'Vendor Management', description: 'Coordinate all your vendors' },
    { icon: <Camera size={32} />, title: 'Photography & Video', description: 'Capture every precious moment' },
    { icon: <Utensils size={32} />, title: 'Catering Services', description: 'Delicious cuisines and menus' },
    { icon: <Music size={32} />, title: 'Entertainment', description: 'Live music, DJs, performers' },
    { icon: <Sparkles size={32} />, title: 'On-Day Management', description: 'Flawless event execution' },
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="py-20 md:py-32 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-amber-600 font-semibold text-base md:text-lg tracking-wide uppercase">
            Our Services
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Complete Event Planning & Management
          </h2>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
            From weddings and family celebrations to corporate events, we handle every detail so you can enjoy stress-free celebrations.
          </p>
        </div>

        {/* Main Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {mainServices.map((service, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 bg-white"
            >
              {/* Image */}
              <div className="relative h-72 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent"></div>
                
                {/* Icon */}
                <div className="absolute top-6 left-6 text-amber-400 bg-white/10 backdrop-blur-md p-4 rounded-2xl">
                  {service.icon}
                </div>
              </div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h3 className="text-2xl md:text-3xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-200 leading-relaxed mb-4 text-sm md:text-base">
                  {service.description}
                </p>
                
                {/* Service Tags */}
                <div className="flex flex-wrap gap-2">
                  {service.services.map((item, idx) => (
                    <span key={idx} className="bg-amber-600/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Services */}
        <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12 mb-12">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
            Additional Services We Offer
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalServices.map((service, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 rounded-xl hover:bg-amber-50 transition-colors duration-300"
              >
                <div className="text-amber-600 flex-shrink-0">
                  {service.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{service.title}</h4>
                  <p className="text-sm text-gray-600">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-3xl shadow-2xl p-8 md:p-12 text-center text-white">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Plan Your Perfect Event?
          </h3>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Let's discuss your event needs and create something beautiful together. Book a consultation with our team today.
          </p>
          <Button
            onClick={scrollToContact}
            className="bg-white text-amber-700 hover:bg-gray-100 px-10 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-xl rounded-full"
          >
            Get a Free Consultation
          </Button>
        </div>

        {/* Service Area */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 text-sm md:text-base">
            Serving <strong className="text-gray-900">Vadodara, Anand, Bharuch, Surat</strong> and across <strong className="text-gray-900">Gujarat</strong>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Services;